import { z } from 'zod';
/**
 * E-CRM04 — "Resume your enrollment".
 *
 * Accounts emails a lead who left the funnel a link of the shape
 * `<storefront>/bff/resume/<token>` (`BFF_ROUTES.resumeEntry`). The BFF redeems
 * the token server-side, mints a FRESH funnel session seeded with what the lead
 * had reached (meter, plan, contact — never identity), and 302s into the
 * storefront carrying `STOREFRONT_RESUME_ARRIVAL_PARAM`.
 *
 * The declarations live here, not as literals on either side, for the reason the
 * offer entry's do (`offer.ts`): the BFF WRITES the storefront URL and the SPA
 * SERVES it, and two hand-written copies of one fact silently disagree the day
 * either moves.
 */
/**
 * Marks a load that ARRIVED FROM A RESUME REDIRECT.
 *
 * Same job, and the same non-control posture, as `STOREFRONT_OFFER_ARRIVAL_PARAM`:
 * `FunnelHydrationGate` asks for `session?fresh=1` on every load, which would
 * delete the session the BFF seeded moments earlier. With this marker the gate
 * keeps the session and reads the snapshot for the meter. It rides in a URL, so it
 * is attacker-controlled and gates nothing: all it can do is stop the browser
 * retiring ITS OWN session.
 */
export const STOREFRONT_RESUME_ARRIVAL_PARAM = 'resumed';
/**
 * The resume token as it appears in the URL path.
 *
 * Accounts mints `randomBytes(32).toString('base64url')` — 43 URL-safe characters.
 * The bound here is deliberately LOOSE (shape, not exact length), exactly like
 * `OfferTokenSchema`: a malformed guess must be refused the SAME way as a
 * well-formed unknown one, so this is not a token-format oracle. It also stays
 * strictly below the BFF's `maxParamLength` (512) so the application, not the
 * router, answers an over-long value.
 */
export const ResumeTokenSchema = z
    .string()
    .min(1)
    .max(256)
    .regex(/^[A-Za-z0-9_-]+$/u, 'resume token must be URL-safe base64');
/** `POST /bff/resume/confirm` — the phone number the lead gave (any formatting). */
export const ResumeConfirmRequestSchema = z
    .object({
    phone: z.string().min(1).max(32),
})
    .strict();
/**
 * The answer to a phone check or a skip.
 *  - `ok: true` + `redirectTo`: restored — go where the lead left off.
 *  - `ok: false` + `attemptsLeft > 0`: that number does not match (HTTP 422).
 *  - `ok: false` + `attemptsLeft: 0` + `redirectTo: '/'`: locked out, expired
 *    or skipped — the held details are discarded; start over.
 * Carries nothing the lead gave us.
 */
export const ResumeConfirmResponseSchema = z.object({
    ok: z.boolean(),
    attemptsLeft: z.number().int().min(0).optional(),
    redirectTo: z.string().startsWith('/').optional(),
});
/**
 * `POST /bff/resume/continue` → where to go. `ok: true` lands where the lead
 * left off (or on the phone check); `ok: false` goes to `/resume/expired` (the
 * link did not redeem, or no token is held) or `/` (skipped).
 */
export const ResumeContinueResponseSchema = z.object({
    ok: z.boolean(),
    redirectTo: z.string().startsWith('/'),
});
//# sourceMappingURL=resume.js.map